/**
 * LegalDocument Page
 *
 * Displays a single legal document (Terms of Service, Privacy Policy, etc.)
 * fetched by slug from the API. Renders HTML content from the database.
 *
 * Usage: <LegalDocument slug="terms-of-service" onBack={() => ...} />
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { ArrowLeft, Calendar, FileText } from 'lucide-react';
import { AxiosError } from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import api from '../api/axiosConfig';

interface LegalDocumentProps {
  /** The slug of the document to load, e.g. 'terms-of-service' */
  slug: string;
  /** Called when user clicks Back — if omitted, Back button is hidden */
  onBack?: () => void;
  /** Optional: called after document is fully loaded (used by external consumers) */
  onLoaded?: (doc: LegalDocumentData) => void;
}

export interface LegalDocumentData {
  id: string;
  slug: string;
  type: string;
  title: string;
  content: string;
  version: string;
  isRequired: boolean;
  effectiveDate: string;
}

export default function LegalDocument({ slug, onBack, onLoaded }: LegalDocumentProps) {
  const [doc, setDoc] = useState<LegalDocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const onLoadedRef = useRef(onLoaded);

  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  const fetchDocument = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await api.get<{ success: boolean; data: LegalDocumentData }>(
        `/legal/documents/${encodeURIComponent(slug)}`
      );
      if (res.data?.success === false) {
        setError('Failed to load document. Please try again.');
        return;
      }
      const data: LegalDocumentData = res.data.data;
      setDoc(data);
      onLoadedRef.current?.(data);
    } catch (err: unknown) {
      const message =
        err instanceof AxiosError
          ? (err.response?.data as { message?: string })?.message || err.message
          : 'Failed to load document. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    setDoc(null);
    fetchDocument();
  }, [slug, fetchDocument]);

  // Format effective date for display
  const formattedDate = doc?.effectiveDate
    ? new Date(doc.effectiveDate).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  // Sanitize HTML content using DOMPurify before rendering.
  // Content originates from our own D1 database (admin-seeded only, not user input),
  // but we sanitize defensively to prevent any XSS in case content is ever edited.
  const sanitizedContent = doc
    ? DOMPurify.sanitize(doc.content, { USE_PROFILES: { html: true } })
    : '';

  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">

        {/* Back button */}
        {onBack && (
          <Button
            variant="ghost"
            size="sm"
            className="mb-6 -ml-2 text-muted-foreground hover:text-foreground"
            onClick={onBack}
          >
            <ArrowLeft className="mr-1 size-4" />
            Back
          </Button>
        )}

        {/* Loading state */}
        {loading && (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
              <p className="text-sm text-muted-foreground">Loading document...</p>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {!loading && error && (
          <Card className="border-destructive/50">
            <CardContent className="py-12 text-center">
              <p className="text-sm text-destructive">{error}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={fetchDocument}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Document content */}
        {!loading && doc && (
          <Card>
            <CardHeader className="border-b pb-6">
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <FileText className="size-5 shrink-0 text-muted-foreground" />
                    <CardTitle className="text-2xl font-bold">{doc.title}</CardTitle>
                  </div>
                  {formattedDate && (
                    <CardDescription className="mt-2 flex items-center gap-1.5">
                      <Calendar className="size-3.5 shrink-0" />
                      Effective {formattedDate}
                    </CardDescription>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Badge variant="secondary" className="font-mono text-xs">
                    v{doc.version}
                  </Badge>
                  {doc.isRequired && (
                    <Badge variant="default" className="text-xs">
                      Required
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent
              className={cn(
                // Prose-style typography for the HTML content
                'prose prose-sm dark:prose-invert max-w-none pt-6',
                'prose-headings:font-semibold prose-headings:text-foreground',
                'prose-h1:text-2xl prose-h2:text-lg prose-h2:mt-6',
                'prose-p:text-muted-foreground prose-p:leading-relaxed',
                'prose-li:text-muted-foreground',
                'prose-strong:text-foreground'
              )}
              // Content is sanitized by DOMPurify above before rendering
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
          </Card>
        )}

      </div>
    </div>
  );
}
